import { ClientSentryEventContext } from '@/shared/utils/sentry/clientIgnoreRules/types';
import { toStr } from '@/shared/utils/sentry/clientIgnoreRules/stringMatchers';

type ErrorLike = {
  message?: unknown;
};

type SentryStackFrameLike = {
  in_app?: unknown;
  filename?: unknown;
};

type SentryStacktraceLike = {
  frames?: unknown;
};

type SentryExceptionValueLike = {
  type?: unknown;
  value?: unknown;
  stacktrace?: unknown;
};

type SentryEventLike = {
  message?: unknown;
  exception?: {
    values?: unknown;
  };
};

type SentryHintLike = {
  originalException?: unknown;
};

const toSentryEventLike = (event: unknown): SentryEventLike | null =>
  event && typeof event === 'object' ? (event as SentryEventLike) : null;

const toSentryHintLike = (hint: unknown): SentryHintLike | null =>
  hint && typeof hint === 'object' ? (hint as SentryHintLike) : null;

const getFirstExceptionValue = (
  event: SentryEventLike | null
): SentryExceptionValueLike | null => {
  const values = event?.exception?.values;
  if (!Array.isArray(values) || values.length === 0) {
    return null;
  }

  const first = values[0];
  return first && typeof first === 'object'
    ? (first as SentryExceptionValueLike)
    : null;
};

const getStackFrames = (
  exceptionValue: SentryExceptionValueLike | null
): SentryStackFrameLike[] => {
  const stacktrace = exceptionValue?.stacktrace as SentryStacktraceLike | null;
  const frames = stacktrace?.frames;

  if (!Array.isArray(frames)) {
    return [];
  }

  return frames.filter(
    (frame): frame is SentryStackFrameLike =>
      Boolean(frame) && typeof frame === 'object'
  );
};

const getOriginalExceptionMessage = (
  originalException: unknown
): string | null => {
  const message = toStr(originalException);
  if (message) {
    return message;
  }

  if (
    originalException &&
    (typeof originalException === 'object' ||
      typeof originalException === 'function')
  ) {
    return toStr((originalException as ErrorLike).message);
  }

  return null;
};

const getStackFrameFilenames = (
  stackFrames: SentryStackFrameLike[]
): string[] =>
  stackFrames
    .map((frame) => toStr(frame.filename))
    .filter((filename): filename is string => Boolean(filename));

export const getClientSentryEventContext = (
  event: unknown,
  hint: unknown
): ClientSentryEventContext => {
  const eventLike = toSentryEventLike(event);
  const hintLike = toSentryHintLike(hint);
  const firstExceptionValue = getFirstExceptionValue(eventLike);
  const stackFrames = getStackFrames(firstExceptionValue);

  return {
    originalExceptionMessage: getOriginalExceptionMessage(
      hintLike?.originalException
    ),
    serializedMessage:
      toStr(firstExceptionValue?.value) ?? toStr(eventLike?.message),
    firstExceptionType: toStr(firstExceptionValue?.type),
    hasExceptionStacktrace: stackFrames.length > 0,
    hasAppFrameInStacktrace: stackFrames.some((frame) => frame.in_app === true),
    stackFrameFilenames: getStackFrameFilenames(stackFrames),
  };
};
